import React, { useMemo } from 'react';
import SearchableDropdown from '../../../components/ui/SearchableDropdown';
import type { TaxCodeForm } from '../coaTemplateApi';

// =============================================
// Tax-return code of one account (country tax pack, migration 145) — e.g. the Canada
// GIFI code of a corporation's / partnership's account. The account number stays the
// user's own; several accounts can share one code. Balance-sheet codes for assets,
// liabilities and equity, income-statement codes otherwise; grouped like the guide,
// and the guide's "includes" words are searched ("hydro" finds 8812 / 9221).
// Used by Setup GL Accounts (StagedAccountModal) and the GL Account form.
// =============================================

/** Balance sheet for assets / liabilities / equity, income statement otherwise. */
export type TaxStatement = 'BS' | 'IS';

/** Is `code` a code of `form` on that statement? ('' is never.) */
export function taxCodeFits(form: TaxCodeForm, statement: TaxStatement, code: string | undefined | null): boolean {
  return !!code && form.lines.some(l => l.line_code === code && l.statement === statement);
}

/**
 * The statement group of a GIFI code by its range (the guide's top headings): Assets,
 * Liabilities, Equity … — the sub-group (current assets, operating expenses) is the
 * line's own line_group.
 */
export function taxCodeTopGroup(code: string): string {
  const n = parseInt(code, 10);
  if (isNaN(n)) return '';
  if (n < 2600) return 'Assets';
  if (n < 3500) return 'Liabilities';
  if (n < 3850) return 'Equity';
  if (n >= 7000 && n < 7100) return 'Other comprehensive income';
  if (n >= 8000 && n < 8300) return 'Revenue';
  if (n >= 8300 && n < 8520) return 'Cost of sales';
  if (n >= 8520 && n < 9370) return 'Operating expenses';
  if (n >= 9370 && n < 9660) return 'Farm revenue';
  if (n >= 9660 && n < 9900) return 'Farm expenses';
  return 'Net income, extraordinary items and taxes';
}

/** "GIFI 1002 — Deposits in Canadian banks …" for a badge's tooltip. */
export function taxCodeTitle(form: TaxCodeForm, code: string): string {
  const line = form.lines.find(l => l.line_code === code);
  return `${form.tax_form_code} ${code}${line ? ` — ${line.line_name}` : ''}`;
}

interface Props {
  form: TaxCodeForm;
  statement: TaxStatement;
  value: string;
  onChange: (code: string) => void;
  /** Class for the dropdown (the screen's own select look). */
  className?: string;
}

const TaxCodePicker: React.FC<Props> = ({ form, statement, value, onChange, className }) => {
  // the account's own wording when it has one (Tax Codes tab), CRA's name otherwise;
  // codes the account hid stay out of the list unless one is this account's code
  const options = useMemo(() => form.lines
    .filter(l => l.statement === statement && (!Number(l.is_hidden) || l.line_code === value))
    .map(l => ({
      value: l.line_code,
      label: `${l.line_code} ${l.own_name || l.line_name}`,
      group: l.line_group ? l.line_group.charAt(0).toUpperCase() + l.line_group.slice(1) : undefined,
      keywords: [l.own_name ? l.line_name : '', l.includes_text || '', l.own_note || ''].filter(Boolean).join('; ') || undefined,
    })), [form, statement, value]);
  const chosen = form.lines.find(l => l.line_code === value);
  // a code of the other statement (the account's type changed) is not this account's code
  const shown = taxCodeFits(form, statement, value) ? value : '';
  return (
    <div className="coa-field-group">
      <label className="coa-field-label">{form.tax_form_code} code</label>
      <SearchableDropdown
        className={className}
        options={options}
        value={shown}
        onChange={v => onChange(v || '')}
        placeholder={`-- ${form.tax_form_code} code for the tax return --`}
      />
      <span className="coa-field-hint">
        {chosen && chosen.own_name ? <>CRA: {chosen.line_code} {chosen.line_name}. </> : null}
        {chosen && chosen.includes_text ? <><strong>Includes:</strong> {chosen.includes_text}. </> : null}
        {form.form_name}{form.version ? ` (${form.version.version_label})` : ''}: the line this account is reported on in the
        {statement === 'BS' ? ' balance sheet' : ' income statement'} of the tax return. Several accounts can share one code.
      </span>
    </div>
  );
};

export default TaxCodePicker;
